import Phaser from 'phaser';
import { CampManager, Survivor, SurvivorClass } from '../systems/CampManager';
import { MetaManager } from '../systems/MetaManager';
import { ExpeditionManager } from '../systems/ExpeditionManager';
import { SfxManager } from '../utils/SfxManager';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { ResourceBanner } from '../ui/ResourceBanner';
import { ExpeditionDefinition, ExpeditionId, EXPEDITION_DEFINITIONS } from '../data/expeditions';
import { BUILDING_DEFINITIONS } from '../data/buildings';
import { CLASS_DEFINITIONS } from '../constants';
import { RESOURCE_INFO, ResourceType } from '../systems/ResourceManager';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';
import { IslandCampFlag } from '../systems/IslandCampFlag';

// === SCÈNE DE LA VIGIE ===
// Permet d'envoyer des survivants en expédition idle pendant le run.

const HEADER_H = 75;

const CLASS_INFO: Record<SurvivorClass, { emoji: string; name: string; color: string }> = {
  warrior: { emoji: '⚔️', name: 'Guerrier', color: '#e74c3c' },
  ranger: { emoji: '🏹', name: 'Tireur', color: '#2ecc71' },
  medic: { emoji: '💊', name: 'Médecin', color: '#3498db' },
};

export class VigieScene extends Phaser.Scene {
  private panel!: ScrollablePanel;
  private camp!: CampManager;
  private meta!: MetaManager;
  private expeditions!: ExpeditionManager;
  private sfx = new SfxManager();

  /** Expédition sélectionnée (null = aucune) */
  private selectedExpedition: ExpeditionId | null = null;
  /** Survivants assignés */
  private assignedIds: string[] = [];

  constructor() {
    super({ key: 'VigieScene' });
  }

  init(data: { camp?: CampManager; meta?: MetaManager }): void {
    this.camp = data?.camp ?? new CampManager();
    this.meta = data?.meta ?? new MetaManager();
    this.expeditions = new ExpeditionManager();
    this.selectedExpedition = null;
    this.assignedIds = [];
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.drawUI();
    this.showFirstVisitTutorial();
  }

  private showFirstVisitTutorial(): void {
    const TUTORIAL_KEY = 'deadtide_vigie_tutorial_seen';
    try {
      if (localStorage.getItem(TUTORIAL_KEY)) return;
      localStorage.setItem(TUTORIAL_KEY, '1');
    } catch { return; }

    // Overlay semi-transparent
    const overlay = this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x000000, 0.65)
      .setDepth(200).setInteractive();

    const popupW = Math.min(500, GAME_W - 30);
    const popup = this.add.rectangle(GAME_CX, GAME_CY - 30, popupW, 280, 0x0d1a2e, 0.97)
      .setStrokeStyle(2, 0xf39c12).setDepth(201);

    const title = this.add.text(GAME_CX, GAME_CY - 145, '🗼 VIGIE — Comment ça marche ?', {
      fontSize: '15px', color: '#f39c12', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(202);

    const lines = [
      '⛵ La Vigie permet d\'envoyer des survivants',
      '   en expédition pendant que vous explorez.',
      '',
      '👥 Choisissez une expédition et assignez',
      '   1 à 3 survivants selon les requis.',
      '',
      '⚠️ Risque de mort : des survivants peuvent',
      '   ne pas revenir. Un score d\'équipe élevé',
      '   réduit le risque.',
      '',
      '🎁 Récompense : ressources et or au retour',
      '   de votre prochain run.',
    ].join('\n');

    const body = this.add.text(GAME_CX, GAME_CY - 95, lines, {
      fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY, lineSpacing: 2,
    }).setOrigin(0.5, 0).setDepth(202);

    const closeBtn = this.add.text(GAME_CX, GAME_CY + 120, '[ J\'ai compris ! ]', {
      fontSize: '14px', color: '#2ecc71', fontFamily: FONT_BODY,
      backgroundColor: '#1a2a1a', padding: { x: 18, y: 8 },
    }).setOrigin(0.5).setDepth(202);
    closeBtn.setInteractive({ useHandCursor: true });

    const tutorialElements = [overlay, popup, title, body, closeBtn];
    const close = () => tutorialElements.forEach(e => e.destroy());
    closeBtn.on('pointerdown', close);
    overlay.on('pointerdown', close);
    closeBtn.on('pointerover', () => closeBtn.setColor('#27ae60'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#2ecc71'));
  }

  private drawUI(): void {
    // Bug #k2qsq — drawUI() est rappelée à chaque interaction (sélection
    // d'expédition, [Assigner], [Retirer]…). Sans détruire l'ancien
    // ScrollablePanel, ses 4 listeners globaux (wheel + pointerdown/move/up)
    // restent abonnés à scene.input et s'accumulent à chaque redraw. Symptôme
    // observable : après [Assigner], le moindre clic n'importe où sur l'écran
    // ré-assigne le survivant à l'infini car des handlers fantômes captent
    // les pointer events et reagissent comme si on cliquait toujours sur
    // l'ancien bouton (qui pointe sur une closure ré-entrante).
    this.panel?.destroy();
    this.children.removeAll(true);

    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // Bandeau ressources
    new ResourceBanner(this, this.camp.getResources());

    // === HEADER FIXE ===
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);

    const vigieLevel = this.camp.getUpgrades().getBuildingLevel('watchtower');
    this.add.text(GAME_CX, 15, '🗼 VIGIE', {
      fontSize: '20px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    this.add.text(GAME_CX, 42, `Niveau ${vigieLevel} — ${BUILDING_DEFINITIONS.watchtower.levels[vigieLevel].label}`, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    // Expédition déjà en cours
    if (this.expeditions.isOnExpedition()) {
      const active = this.expeditions.getActive()!;
      const aDef = EXPEDITION_DEFINITIONS[active.expeditionId];
      this.add.text(GAME_CX, 58, `${aDef.emoji} Expédition en cours : ${aDef.name} (${active.survivorIds.length} survivant(s))`, {
        fontSize: '10px', color: '#e67e22', fontFamily: FONT_BODY,
      }).setOrigin(0.5).setDepth(11);
    }

    // Retour camp
    const backBtn = this.add.text(15, 10, '← Camp', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 10, y: 6 },
    }).setDepth(11);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => startSceneWithFade(this, IslandCampFlag.campSceneKey(), { camp: this.camp, meta: this.meta }));
    backBtn.on('pointerover', () => backBtn.setColor('#ff6b6b'));
    backBtn.on('pointerout', () => backBtn.setColor('#e74c3c'));

    // === CONTENU SCROLLABLE ===
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H - 24);
    let y = 10;

    if (vigieLevel === 0) {
      this.panel.add(this.add.text(GAME_CX, 50, '🗼 La Vigie n\'est pas encore construite.\n\nAméliorez-la dans l\'Atelier\npour débloquer les expéditions.', {
        fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY, align: 'center',
      }).setOrigin(0.5));
      y = 130;
    } else if (this.expeditions.isOnExpedition()) {
      y = this.drawActiveExpedition(y);
    } else {
      // Liste des expéditions
      y = this.drawExpeditionList(y);

      // Si une expédition est sélectionnée, afficher l'assignation
      if (this.selectedExpedition) {
        y += 10;
        y = this.drawAssignment(y);
      }
    }

    y += 30;
    this.panel.setContentHeight(y);
  }

  // === LISTE DES EXPÉDITIONS ===

  private drawExpeditionList(y: number): number {
    const available = this.expeditions.getAvailable(this.camp.getUpgrades());

    // Bug fix : encart explicatif pour clarifier le système de la Vigie
    const rW = Math.min(560, GAME_W - 10);
    const helpBg = this.add.rectangle(GAME_CX, y + 50, rW, 100, 0x1a2540).setStrokeStyle(1, 0x2a3a5c);
    this.panel.add(helpBg);
    this.panel.add(this.add.text(GAME_CX, y + 8, '🗼 Comment ça marche ?', {
      fontSize: '12px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5));
    const helpText = [
      '• Envoie 1-3 survivants en expédition idle pendant ton run',
      '• Le résultat se calcule à ton retour de run (récompenses + risque mort)',
      '• Score équipe = ΣHP + ATK×2 + DEF×2 + niveau×5 → réduit le risque',
      '• +1 niveau par expé réussie sans morts (cap niv.5)',
    ].join('\n');
    this.panel.add(this.add.text(GAME_CX, y + 28, helpText, {
      fontSize: '10px', color: '#bdc3c7', fontFamily: FONT_BODY, align: 'center', lineSpacing: 2,
    }).setOrigin(0.5, 0));
    y += 110;

    const headerX = GAME_CX - Math.min(560, GAME_W - 20) / 2 + 10;
    this.panel.add(this.add.text(headerX, y, '--- Expéditions disponibles ---', {
      fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
    }));
    y += 22;

    for (const exp of available) {
      y = this.drawExpeditionRow(exp, y);
      y += 8;
    }

    return y;
  }

  private drawExpeditionRow(exp: ExpeditionDefinition, y: number): number {
    const isSelected = this.selectedExpedition === exp.id;

    // Layout responsive — desktop x=140 / x=520 / x=530 préservés via formules.
    const isMobile = GAME_W < 600;
    const cardW = Math.min(560, GAME_W - 20);
    const lx = GAME_CX - cardW / 2 + 20;          // 140 desktop, 30 mobile
    const riskX = GAME_CX + cardW / 2 - 160;      // 520 desktop, 280 mobile
    const btnX = GAME_CX + cardW / 2 - 150;       // 530 desktop, 290 mobile
    // Sur mobile la rangée passe à 80 px (rewards passe en dessous pour ne pas chevaucher).
    const rowH = isMobile ? 80 : 55;

    const bg = this.add.rectangle(GAME_CX, y + rowH / 2, cardW, rowH, isSelected ? 0x1a3a2a : 0x1e2d4a);
    bg.setStrokeStyle(isSelected ? 2 : 1, isSelected ? 0x2ecc71 : 0x2a3a5c);
    this.panel.add(bg);

    // Nom
    this.panel.add(this.add.text(lx, y + 5, `${exp.emoji} ${exp.name}`, {
      fontSize: '13px', color: isSelected ? '#2ecc71' : '#ecf0f1', fontFamily: FONT_BODY, fontStyle: 'bold',
    }));

    // Description (cap pour ne pas chevaucher la colonne risque à droite)
    this.panel.add(this.add.text(lx, y + 22, exp.description, {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      wordWrap: { width: riskX - lx - 10 },
    }));

    // Infos : récompenses + risque
    const rewardParts: string[] = [];
    for (const r of exp.resources) {
      const info = RESOURCE_INFO[r.type];
      const chanceStr = r.chance && r.chance < 1 ? ` (${Math.round(r.chance * 100)}%)` : '';
      rewardParts.push(`${info.emoji} ${info.name} ${r.amount.min}-${r.amount.max}${chanceStr}`);
    }
    rewardParts.push(`💰${exp.gold.min}-${exp.gold.max}`);

    // Sur mobile : rewards sur ligne supplémentaire pour respirer
    const rewardsY = isMobile ? y + 50 : y + 36;
    this.panel.add(this.add.text(lx, rewardsY, rewardParts.join('  '), {
      fontSize: '10px', color: '#bdc3c7', fontFamily: FONT_BODY,
      wordWrap: { width: cardW - 40 },
    }));

    // Risque
    const riskLabel = exp.deathRisk.max === 0 ? '☮️ 0%' : `☠️ ${exp.deathRisk.min}-${exp.deathRisk.max}%`;
    const riskColor = exp.deathRisk.max === 0 ? '#2ecc71' : exp.deathRisk.max <= 15 ? '#e67e22' : '#e74c3c';
    this.panel.add(this.add.text(riskX, y + 5, riskLabel, {
      fontSize: '10px', color: riskColor, fontFamily: FONT_BODY,
    }));

    // Équipe requise
    this.panel.add(this.add.text(riskX, y + 20, `👥 ${exp.minSurvivors}-${exp.maxSurvivors}`, {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }));

    // Bouton sélectionner
    if (!isSelected) {
      const btn = this.add.text(btnX, y + 35, '[Choisir]', {
        fontSize: '10px', color: '#f39c12', fontFamily: FONT_BODY,
        backgroundColor: '#2c2a1a', padding: { x: 3, y: 2 },
      });
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        this.sfx.select();
        this.selectedExpedition = exp.id;
        this.assignedIds = [];
        this.drawUI();
      });
      this.panel.add(btn);
    }

    return y + rowH;
  }

  // === ASSIGNATION DES SURVIVANTS ===

  private drawAssignment(y: number): number {
    const expDef = EXPEDITION_DEFINITIONS[this.selectedExpedition!];
    const aliveSurvivors = this.camp.getAliveSurvivors();

    // Layout responsive — desktop x=130/140 préservés via formules (cardW=540).
    const cardW = Math.min(540, GAME_W - 20);
    const headerX = GAME_CX - cardW / 2 + 0;       // 130 desktop, 5 mobile
    const lx = GAME_CX - cardW / 2 + 10;           // 140 desktop, 15 mobile
    const wrapW = cardW - 20;

    this.panel.add(this.add.text(headerX, y, `--- Assigner l'équipe (${this.assignedIds.length}/${expDef.maxSurvivors}) ---`, {
      fontSize: '11px', color: '#f39c12', fontFamily: FONT_BODY,
    }));
    y += 22;

    // Sprint 84 — Aperçu détaillé pré-lancement (formule visible, indicateur de risque)
    if (this.assignedIds.length > 0) {
      const assignedSurvivors = this.assignedIds
        .map(id => aliveSurvivors.find(s => s.id === id))
        .filter((s): s is Survivor => s !== undefined);
      const risk = this.expeditions.getDeathRisk(this.selectedExpedition!, assignedSurvivors);
      const score = this.expeditions.getTeamScore(assignedSurvivors);

      // Indicateur de risque (palier qualitatif)
      let riskLabel: string;
      let riskColor: string;
      if (risk === 0) { riskLabel = '💪 Sans danger'; riskColor = '#2ecc71'; }
      else if (risk < 5) { riskLabel = '💪 Solide'; riskColor = '#2ecc71'; }
      else if (risk < 15) { riskLabel = '⚠️ Risqué'; riskColor = '#e67e22'; }
      else if (risk < 30) { riskLabel = '🔥 Dangereux'; riskColor = '#e74c3c'; }
      else { riskLabel = '💀 Suicide'; riskColor = '#c0392b'; }

      // Cadre récap
      const recapBg = this.add.rectangle(GAME_CX, y + 38, cardW, 78, 0x1a2a40)
        .setStrokeStyle(1, 0x2a3a5c);
      this.panel.add(recapBg);

      // Décomposition du score : par membre
      const breakdown = assignedSurvivors.map(s => {
        const sScore = this.expeditions.getSurvivorScore(s);
        return `${s.name.split(' ')[0]} (${sScore})`;
      }).join(' + ');
      this.panel.add(this.add.text(lx, y + 4, `📊 Score équipe : ${score}`, {
        fontSize: '11px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
      }));
      this.panel.add(this.add.text(lx, y + 18, `   = ${breakdown}`, {
        fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
        wordWrap: { width: wrapW },
      }));

      // Risque + label palier
      this.panel.add(this.add.text(lx, y + 36, `${riskLabel} — Risque mort/survivant : ${risk.toFixed(1)}%`, {
        fontSize: '11px', color: riskColor, fontFamily: FONT_BODY, fontStyle: 'bold',
        wordWrap: { width: wrapW },
      }));

      // Aide formule
      this.panel.add(this.add.text(lx, y + 52, `   Score perso = HP + ATK×2 + DEF×2  (seuil expé : ${EXPEDITION_DEFINITIONS[this.selectedExpedition!].scoreThreshold})`, {
        fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
        wordWrap: { width: wrapW },
      }));

      y += 86;
    }

    // Liste des survivants disponibles (pas en run)
    if (aliveSurvivors.length === 0) {
      this.panel.add(this.add.text(GAME_CX, y + 10, 'Aucun survivant disponible.', {
        fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5));
      y += 30;
    } else {
      for (const s of aliveSurvivors) {
        const isAssigned = this.assignedIds.includes(s.id);
        y = this.drawSurvivorAssignment(s, y, isAssigned, expDef);
        y += 6;
      }
    }

    // Bouton lancer
    y += 10;
    const canLaunch = this.assignedIds.length >= expDef.minSurvivors
      && this.assignedIds.length <= expDef.maxSurvivors;

    if (canLaunch) {
      const launchBtn = this.add.text(GAME_CX, y, `[ ${expDef.emoji} Lancer l'expédition ]`, {
        fontSize: '14px', color: '#2ecc71', fontFamily: FONT_BODY,
        backgroundColor: '#1a2a1a', padding: { x: 16, y: 8 },
      }).setOrigin(0.5);
      launchBtn.setInteractive({ useHandCursor: true });
      launchBtn.on('pointerdown', () => this.launchExpedition());
      launchBtn.on('pointerover', () => launchBtn.setColor('#27ae60'));
      launchBtn.on('pointerout', () => launchBtn.setColor('#2ecc71'));
      this.panel.add(launchBtn);
      y += 40;
    } else {
      this.panel.add(this.add.text(GAME_CX, y, `Assignez ${expDef.minSurvivors}+ survivant(s) pour lancer`, {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      }).setOrigin(0.5));
      y += 25;
    }

    return y;
  }

  private drawSurvivorAssignment(survivor: Survivor, y: number, isAssigned: boolean, expDef: ExpeditionDefinition): number {
    const info = CLASS_INFO[survivor.class];
    const score = this.expeditions.getSurvivorScore(survivor);

    // Layout responsive (cardW=540 desktop). Sur mobile, on stack le bouton
    // sous la ligne nom/score pour libérer la largeur.
    const isMobile = GAME_W < 600;
    const cardW = Math.min(540, GAME_W - 20);
    const lx = GAME_CX - cardW / 2 + 10;            // 140 desktop, 15 mobile
    const scoreX = GAME_CX + cardW / 2 - 220;       // 450 desktop, 5 mobile (cap appliqué plus bas)
    const btnX = GAME_CX + cardW / 2 - 100;         // 570 desktop, 120 mobile (origin droite mobile)
    const fullX = GAME_CX + cardW / 2 - 90;         // 580 desktop, 130 mobile
    const rowH = isMobile ? 50 : 32;

    const bg = this.add.rectangle(GAME_CX, y + rowH / 2 - 2, cardW, rowH, isAssigned ? 0x1a3a2a : 0x1e2d4a);
    bg.setStrokeStyle(isAssigned ? 2 : 1, isAssigned ? 0x2ecc71 : 0x2a3a5c);
    this.panel.add(bg);

    // Étoiles selon le niveau (★ rempli pour level, ☆ vide pour cap atteint)
    const stars = '★'.repeat(Math.min(5, survivor.level)) + '☆'.repeat(Math.max(0, 5 - survivor.level));
    this.panel.add(this.add.text(lx, y + 4, `${info.emoji} ${survivor.name} — ${info.name}  Niv.${survivor.level}  ${stars}`, {
      fontSize: '11px', color: isAssigned ? '#2ecc71' : '#bdc3c7', fontFamily: FONT_BODY,
      wordWrap: { width: cardW - 30 },
    }));

    // Score : à droite sur desktop, sous le nom sur mobile
    const levelBonus = (survivor.level - 1) * 5;
    const bonusStr = levelBonus > 0 ? ` (+${levelBonus} niv)` : '';
    const scoreTextX = isMobile ? lx : scoreX;
    const scoreTextY = isMobile ? y + 22 : y + 6;
    this.panel.add(this.add.text(scoreTextX, scoreTextY, `Score: ${score}${bonusStr}`, {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }));

    // Bouton : à droite sur desktop, en bas-droite sur mobile (origin droite)
    const btnY = isMobile ? y + 22 : y + 3;
    const useRightOrigin = isMobile;
    if (isAssigned) {
      const btn = this.add.text(isMobile ? GAME_CX + cardW / 2 - 10 : btnX, btnY, '[Retirer]', {
        fontSize: '10px', color: '#e74c3c', fontFamily: FONT_BODY,
        backgroundColor: '#2c1a1a', padding: { x: 3, y: 2 },
      });
      if (useRightOrigin) btn.setOrigin(1, 0);
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        this.sfx.select();
        this.assignedIds = this.assignedIds.filter(id => id !== survivor.id);
        this.drawUI();
      });
      this.panel.add(btn);
    } else if (this.assignedIds.length < expDef.maxSurvivors) {
      const btn = this.add.text(isMobile ? GAME_CX + cardW / 2 - 10 : btnX, btnY, '[Assigner]', {
        fontSize: '10px', color: '#f39c12', fontFamily: FONT_BODY,
        backgroundColor: '#2c2a1a', padding: { x: 3, y: 2 },
      });
      if (useRightOrigin) btn.setOrigin(1, 0);
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        this.sfx.select();
        this.assignedIds.push(survivor.id);
        this.drawUI();
      });
      this.panel.add(btn);
    } else {
      const fullText = this.add.text(isMobile ? GAME_CX + cardW / 2 - 10 : fullX, btnY + 2, 'Plein', {
        fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
      });
      if (useRightOrigin) fullText.setOrigin(1, 0);
      this.panel.add(fullText);
    }

    return y + rowH;
  }

  private launchExpedition(): void {
    if (!this.selectedExpedition) return;
    const ok = this.expeditions.startExpedition(this.selectedExpedition, this.assignedIds);
    if (!ok) return;
    this.sfx.expedition();
    this.drawUI();
  }

  /** Affiche l'expédition en cours avec ses détails + bouton rappel */
  private drawActiveExpedition(y: number): number {
    const active = this.expeditions.getActive()!;
    const def = EXPEDITION_DEFINITIONS[active.expeditionId];
    const rW = Math.min(560, GAME_W - 10);

    this.panel.add(this.add.text(GAME_CX, y, '--- Expédition en cours ---', {
      fontSize: '11px', color: '#e67e22', fontFamily: FONT_BODY,
    }).setOrigin(0.5));
    y += 22;

    // Carte expédition
    const bg = this.add.rectangle(GAME_CX, y + 35, rW, 70, 0x2a1a1e).setStrokeStyle(2, 0xe67e22);
    this.panel.add(bg);

    this.panel.add(this.add.text(GAME_CX, y + 10, `${def.emoji} ${def.name}`, {
      fontSize: '14px', color: '#f39c12', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5));

    const survivorNames = active.survivorIds
      .map(id => this.camp.getSurvivors().find(s => s.id === id)?.name ?? '???')
      .join(', ');
    this.panel.add(this.add.text(GAME_CX, y + 32, `👥 ${survivorNames}`, {
      fontSize: '10px', color: '#bdc3c7', fontFamily: FONT_BODY,
      wordWrap: { width: rW - 20 }, align: 'center',
    }).setOrigin(0.5, 0));

    this.panel.add(this.add.text(GAME_CX, y + 52, 'Le résultat sera calculé au retour de votre prochain run.', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5));

    y += 80;

    // Bouton rappeler (annule et rend les survivants disponibles, sans récompense)
    const recallBtn = this.add.text(GAME_CX, y + 10, '[ ⏪ Rappeler l\'équipe (sans récompense) ]', {
      fontSize: '12px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 12, y: 6 },
    }).setOrigin(0.5);
    recallBtn.setInteractive({ useHandCursor: true });
    recallBtn.on('pointerdown', () => this.confirmCancelExpedition(recallBtn));
    recallBtn.on('pointerover', () => recallBtn.setColor('#ff6b6b'));
    recallBtn.on('pointerout', () => recallBtn.setColor('#e74c3c'));
    this.panel.add(recallBtn);

    return y + 40;
  }

  /** Confirmation en deux temps pour annuler l'expédition */
  private confirmCancelExpedition(btn: Phaser.GameObjects.Text): void {
    if (btn.getData('confirm')) {
      this.sfx.select();
      this.expeditions.cancelExpedition();
      this.selectedExpedition = null;
      this.assignedIds = [];
      this.drawUI();
      return;
    }
    btn.setData('confirm', true);
    btn.setText('⚠️ Confirmer le rappel ?');
    btn.setStyle({ color: '#ff6b6b' });
    this.time.delayedCall(3000, () => {
      if (btn.active) {
        btn.setData('confirm', false);
        btn.setText('[ ⏪ Rappeler l\'équipe (sans récompense) ]');
        btn.setStyle({ color: '#e74c3c' });
      }
    });
  }

  shutdown(): void {
    this.panel?.destroy();
  }
}
